import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useContractData } from './contract-view/useContractData';
import { useContractStatus } from './contract-view/useContractStatus';
import { ContractViewMain } from './contract-view/ContractViewMain';
import { PageTitle } from '../ui/PageTitle';
import { ContractViewSkeleton } from '../ui/Skeleton';
import { PDFViewer } from '../ui/PDFViewer';
import { entranceAnimations } from '../../utils/animations';
import { useContracts } from '../../contexts/ContractContext';
import { getDocumentLabel } from '../../utils/documentUtils';

const ContractView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [showSkeletons, setShowSkeletons] = useState(true);
  const [showContent, setShowContent] = useState(false);
  const [isPDFViewerOpen, setIsPDFViewerOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  
  const { loading } = useContracts();
  const { contract, setContract, deleteContract } = useContractData(id!);
  const { statusConfig, priorityInfo, urgencyAlert } = useContractStatus(contract ?? undefined);

  useEffect(() => {
    if (!loading && contract) {
      // Delay inicial para os skeletons aparecerem gradualmente
      setTimeout(() => {
        setShowSkeletons(false);
        // A contagem começa imediatamente quando o skeleton desaparece
        setShowContent(true);
      }, 150);
    }
  }, [loading, contract]);

  useEffect(() => {
    if (!showSkeletons) {
      // Delay de 1.3s para as animações de entrada dos componentes
      setTimeout(() => {
        // Aqui não precisamos mais setar showContent, pois já foi setado
      }, 1300);
    }
  }, [showSkeletons]);

  const handleEdit = () => {
    navigate(`/admin/contratos/editar/${id}`);
  };

  const handleDelete = async () => {
    if (window.confirm('Tem certeza que deseja excluir este contrato?')) {
      try {
        deleteContract(id!);
        navigate('/admin/contratos');
      } catch (error) {
        console.error('Erro ao excluir contrato:', error);
      }
    }
  };

  const handleMountBudget = () => {
    navigate(`/admin/contratos/orcamento/${id}`);
  };

  if (loading || showSkeletons || !contract) {
    return (
      <div className="h-auto bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
        <div className="max-w-none mx-auto">
          <ContractViewSkeleton />
        </div>
      </div>
    );
  }

  return (
    <div className="h-auto bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      <div className="max-w-none mx-auto">
        {/* Page Header com animação de entrada */}
        <div className={`${entranceAnimations.slideInFromTop} ${showContent ? 'animate-[slideInFromTop2_0.3s_ease-out_forwards]' : ''}`}>
          <PageTitle
            title="Visualizar Contrato"
            subtitle="Gerencie todos os seus contratos de eventos"
          >
            <button
              onClick={() => navigate('/admin/contratos')}
              className="px-4 py-2.5 bg-white/20 backdrop-blur-sm text-white rounded-xl hover:bg-white/30 flex items-center gap-2 font-semibold border border-white/30 transition-all duration-200"
            >
              <ArrowLeft className="w-4 h-4" />
              Voltar aos Contratos
            </button>
          </PageTitle>
        </div>

        {/* Espaçamento entre header e conteúdo */}
        <div className="h-4"></div>

        {/* Main Content com animação de entrada */}
        <div className={`${entranceAnimations.fadeIn} ${showContent ? 'animate-[fadeIn_0.4s_ease-out_forwards]' : ''}`}>
          <ContractViewMain
            contract={contract}
            statusConfig={statusConfig}
            priorityInfo={priorityInfo}
            urgencyAlert={urgencyAlert}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onContractUpdate={setContract}
            showContent={showContent}
            onOpenPDFViewer={(page) => {
              setCurrentPage(page);
              setIsPDFViewerOpen(true);
            }}
            onMountBudget={handleMountBudget}
          />
        </div>

        {/* Visualizador de PDF - Agora no nível principal */}
        <PDFViewer
          isOpen={isPDFViewerOpen}
          onClose={() => setIsPDFViewerOpen(false)}
          contractId={contract.id?.toString()}
          title={`Contrato - ${contract.contratante}`}
          contract={contract}
          initialPage={currentPage}
          renderPageFunction={(pageNumber) => {
            // Estilos base para fonte Courier - IDÊNTICOS aos da página
            const courierBase = { fontFamily: 'Courier, "Courier New", monospace' };
            const courierBold = { ...courierBase, fontWeight: 'bold' };
            const courierNormal = { ...courierBase, fontWeight: 'normal' };

            // Função para formatar data por extenso - IDÊNTICA à da página
            const formatDateLong = (dateString: string) => {
              if (!dateString) return '';
              
              try {
                let date: Date;
                
                // Se a data está em formato brasileiro (DD/MM/YYYY)
                if (dateString.includes('/')) {
                  const [day, month, year] = dateString.split('/');
                  date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
                } else if (dateString.includes('-')) {
                  // Se a data está em formato ISO (YYYY-MM-DD)
                  date = new Date(dateString + 'T00:00:00');
                } else {
                  // Outros formatos
                  date = new Date(dateString);
                }
                
                // Verificar se a data é válida
                if (isNaN(date.getTime())) {
                  return 'Data inválida';
                }
                
                const dia = date.getDate();
                const mes = date.toLocaleString('pt-BR', { month: 'long' });
                const ano = date.getFullYear();
                return `${dia} de ${mes} de ${ano}`;
              } catch (error) {
                console.error('Erro ao formatar data:', error, 'Data original:', dateString);
                return 'Data inválida';
              }
            };

            // Função para formatar valor por extenso - IDÊNTICA à da página
            const formatValueLong = (value: string | undefined) => {
              if (!value) return '';

              const numValue = parseFloat(value.replace(',', '.'));

              if (!isNaN(numValue)) {
                const formattedNumber = numValue.toLocaleString('pt-BR', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                });
                return `${formattedNumber} REAIS`;
              }

              return value;
            };

            const getMusicalRestrictionText = (value?: string) => {
              const cleaned = (value || '').trim();
              if (!cleaned || /^\d+([,.]\d{1,2})?$/.test(cleaned)) {
                return 'NAO POSSUEM';
              }

              return cleaned.toUpperCase();
            };
            // Função para renderizar página específica - IDÊNTICA à da página
            const renderPage = (pageNumber: number) => {
              switch (pageNumber) {
                case 1:
                  return (
                    <div className="bg-white shadow-lg border border-slate-200 rounded-lg overflow-hidden relative" 
                         style={{ width: '210mm', height: '297mm', transform: 'scale(1)', transformOrigin: 'top left' }}>
                      {/* Imagem de Cabeçalho */}
                      <img 
                        src="/images/cabecalho.jpg" 
                        alt="Cabeçalho" 
                        className="w-full h-auto"
                        style={{ display: 'block' }}
                      />
                      
                      <div style={{ padding: '12mm', paddingBottom: '5px', ...courierNormal, fontSize: '10pt', lineHeight: '1.4' }}>
                        {/* Cabeçalho */}
                        <div className="text-center mb-4">
                          <h1 style={{ ...courierBold, fontSize: '10pt', marginBottom: '4px' }}>
                            CONTRATO DE PRESTAÇÃO DE SERVIÇO DE RECREAÇÃO INFANTIL
                          </h1>
                        </div>

                        {/* Identificação das Partes */}
                        <div className="mb-1">
                          <h2 style={{ ...courierBold, fontSize: '10pt', marginBottom: '6px' }}>
                            IDENTIFICAÇÃO DAS PARTES CONTRATANTES
                          </h2>
                          
                          {/* Contratante */}
                          <div className="mb-1">
                            <p style={{ ...courierNormal, textAlign: 'justify', marginBottom: '4px' }}>
                              <span style={courierBold}>CONTRATANTE:</span> <span style={{ backgroundColor: '#FFEB3B', padding: '2px 4px', borderRadius: '3px', fontWeight: 'bold' }}>{contract.contratante?.toUpperCase()}</span>, {contract.nacionalidade?.toUpperCase() || "BRASILEIRO"}, {contract.estadoCivil?.toUpperCase()}, {contract.profissao?.toUpperCase() || "DO LAR"}{contract.identidade ? `, RG nº ${contract.identidade}` : ''}, {(() => {
                                // Verificar se tem CNPJ primeiro (prioridade)
                                if (contract.cnpj && contract.cnpj.trim() !== '') {
                                  return `CNPJ nº ${contract.cnpj}`;
                                }
                                // Se não tem CNPJ, verificar CPF
                                if (contract.cpf && contract.cpf.trim() !== '') {
                                  return `C.P.F. nº ${contract.cpf}`;
                                }
                                // Se não tem nenhum, mostrar N/A
                                return 'C.P.F. nº N/A';
                              })()}, Tel. {contract.telefone}, residente e domiciliado na {contract.endereco?.toUpperCase()}, nº {contract.numero}
                              {contract.complemento && `, ${contract.complemento.toUpperCase()}`}
                              , Bairro {contract.bairro?.toUpperCase()}, Cep {contract.cep}, Cidade {contract.cidade?.toUpperCase()}, no Estado {contract.estado?.toUpperCase()}.
                            </p>
                          </div>

                          {/* Contratada */}
                          <div className="mb-1">
                            <p style={{ ...courierNormal, textAlign: 'justify', marginBottom: '4px' }}>
                              <span style={courierBold}>CONTRATADA:</span> Play Kids Recreação Infantil, com sede em Manhuaçu, na Rua Faustino Amâncio, nº 91, bairro Santo Antônio, Cep 36.900-000, no Estado de MG, C.N.P.J. 31.692.815/0001-02, neste ato representada pelo seu diretor RODRIGO PEREIRA DE OLIVEIRA, brasileiro, Casado, Professor, Carteira de Identidade nº MG 12 682 959, C.P.F. nº 016.331.906-50, residente e domiciliado na Rua Faustino Amâncio, nº 91 bairro Santo Antônio, Cep 36.900-157, Cidade Manhuaçu, no Estado MG.
                            </p>
                          </div>

                          <p style={{ ...courierNormal, textAlign: 'justify', marginBottom: '4px' }}>
                            As partes acima identificadas têm, entre si, justo e acertado o presente Contrato de Prestação de Serviço de Recreação Infantil, que se regerá pelas cláusulas seguintes e pelas condições de pagamento descritas no presente.
                          </p>
                        </div>

                        {/* Objeto do Contrato */}
                        <div className="mb-1">
                          <h2 style={{ ...courierBold, fontSize: '10pt', marginBottom: '6px' }}>
                            DO OBJETO DO CONTRATO
                          </h2>
                          
                          <div className="mb-1">
                            <p style={{ ...courierNormal, textAlign: 'justify', marginBottom: '4px' }}>
                              <span style={courierBold}>CLÁUSULA 1ª</span> - O presente instrumento tem como objeto a prestação de serviços de recreação infantil.
                            </p>
                          </div>

                          <div className="mb-1">
                            <p style={{ ...courierNormal, textAlign: 'justify', marginBottom: '4px' }}>
                              <span style={courierBold}>CLÁUSULA 2ª</span> - O Pacote escolhido foi o: {(contract.pacoteAtividades || "PERSONALIZADO").toUpperCase()}, contendo as seguintes atividades:
                            </p>
                          </div>

                          <div className="mb-1">
                            <p style={{ ...courierBold, textAlign: 'justify', marginBottom: '4px' }}>
                              {(contract.temaFesta || "TEMA PERSONALIZADO").toUpperCase()}
                            </p>
                          </div>

                          {contract.observacoes && contract.observacoes.trim() !== '' && (
                            <div className="mb-1">
                              <p style={{ ...courierNormal, textAlign: 'justify', marginBottom: '4px' }}>
                                <span style={courierBold}>Observações:</span> <span style={{ backgroundColor: '#FFEB3B', padding: '2px 4px', borderRadius: '3px', fontWeight: 'bold' }}>{contract.observacoes.replace(/\*([^*]+)\*/g, '$1').replace(/[`Ø<B^]/g, '').replace(/🎈|🤡/g, '').replace(/[^\w\s\-.,:;!?()áàâãéèêíìîóòôõúùûçÁÀÂÃÉÈÊÍÌÎÓÒÔÕÚÙÛÇ]/g, '').replace(/\s+/g, ' ').trim()}</span>
                              </p>
                            </div>
                          )}
                        </div>

                        {/* Da Festa */}
                        <div className="mb-1">
                          <h2 style={{ ...courierBold, fontSize: '10pt', marginBottom: '6px' }}>
                            DA FESTA
                          </h2>
                          
                          <div className="mb-1">
                            <p style={{ ...courierNormal, textAlign: 'justify', marginBottom: '4px' }}>
                              <span style={courierBold}>CLÁUSULA 3ª</span> - A festa será realizada no dia <span style={{ backgroundColor: '#FFEB3B', padding: '2px 4px', borderRadius: '3px', fontWeight: 'bold' }}>{contract.dataFesta && formatDateLong(contract.dataFesta)}</span> às <span style={{ backgroundColor: '#FFEB3B', padding: '2px 4px', borderRadius: '3px', fontWeight: 'bold' }}>{contract.horarioInicioFesta}</span> horas, no(a) endereço <span style={{ backgroundColor: '#FFEB3B', padding: '2px 4px', borderRadius: '3px', fontWeight: 'bold' }}>{(contract.enderecoFesta || contract.endereco)?.toUpperCase()}</span>.
                            </p>
                          </div>

                          <div className="mb-1">
                            <p style={{ ...courierNormal, textAlign: 'justify', marginBottom: '4px' }}>
                              <span style={courierBold}>CLÁUSULA 4ª</span> - A recreação terá início às <span style={{ backgroundColor: '#FFEB3B', padding: '2px 4px', borderRadius: '3px', fontWeight: 'bold' }}>{contract.inicioRecreacao || contract.horarioInicioFesta}</span> horas, encerrando-se às <span style={{ backgroundColor: '#FFEB3B', padding: '2px 4px', borderRadius: '3px', fontWeight: 'bold' }}>{contract.fimRecreacao || (() => {
                                const startTime = contract.inicioRecreacao || contract.horarioInicioFesta;
                                if (!startTime) return 'N/A';
                                const [hours, minutes] = startTime.split(':').map(Number);
                                const startMinutes = hours * 60 + minutes;
                                const endMinutes = startMinutes + 90; // 1h30min = 90 minutos
                                const endHours = Math.floor(endMinutes / 60);
                                const endMins = endMinutes % 60;
                                return `${endHours.toString().padStart(2, '0')}:${endMins.toString().padStart(2, '0')}`;
                              })()}</span> horas.
                            </p>
                          </div>

                          <div className="mb-1">
                            <p style={{ ...courierNormal, textAlign: 'justify', marginBottom: '4px' }}>
                              <span style={courierBold}>Parágrafo único.</span> O tempo previsto para início e término do serviço deverá ser respeitado e em caso de atraso no início por parte do CONTRATANTE, a CONTRATADA se reserva o direito de encerrar as atividades no horário previsto.
                            </p>
                          </div>

                          <div className="mb-1">
                            <p style={{ ...courierNormal, textAlign: 'justify', marginBottom: '4px' }}>
                              <span style={courierBold}>CLÁUSULA 5ª</span> - Se o CONTRATANTE desejar ampliar o horário da recreação, deverá ser em comum acordo com a CONTRATADA, e caso haja acordo, será cobrada taxa extra no valor de R$ 450,00. (não ficamos fração de hora).
                            </p>
                          </div>

                          <div className="mb-1">
                            <p style={{ ...courierNormal, textAlign: 'justify', marginBottom: '4px' }}>
                              <span style={courierBold}>CLÁUSULA 6ª</span> - A quantidade de crianças será de aproximadamente {contract.numeroConvidados}, devendo este número ser comunicado à CONTRATADA com 5 dias de antecedência.
                            </p>
                          </div>

                          <div className="mb-1">
                            <p style={{ ...courierNormal, textAlign: 'justify', marginBottom: '4px' }}>
                              <span style={courierBold}>CLÁUSULA 7ª</span> - O aniversariante chama-se {contract.nomeAniversariante?.toUpperCase()}, o tema da festa é {contract.temaFesta?.toUpperCase()}, os pais do aniversariante {getMusicalRestrictionText(contract.restricaoMusical)} restrição a músicas.
                            </p>
                          </div>
                        </div>
                      </div>
                      
                      {/* Imagem de Rodapé */}
                      <img 
                        src="/images/rodape.jpg" 
                        alt="Rodapé" 
                        className="absolute bottom-0 left-0 w-full h-auto"
                        style={{ display: 'block' }}
                      />
                    </div>
                  );

                case 2:
                  return (
                    <div className="bg-white shadow-lg border border-slate-200 rounded-lg overflow-hidden relative" 
                         style={{ width: '210mm', height: '297mm', transform: 'scale(1)', transformOrigin: 'top left' }}>
                      {/* Imagem de Cabeçalho */}
                      <img 
                        src="/images/cabecalho.jpg" 
                        alt="Cabeçalho" 
                        className="w-full h-auto"
                        style={{ display: 'block' }}
                      />
                      
                      <div style={{ padding: '12mm', paddingBottom: '5px', ...courierNormal, fontSize: '10pt', lineHeight: '1.4' }}>
                        {/* Obrigações do Contratante */}
                        <div className="mb-1">
                          <h2 style={{ ...courierBold, fontSize: '10pt', marginBottom: '6px' }}>
                            DAS OBRIGAÇÕES DO CONTRATANTE
                          </h2>
                          
                          <div className="mb-1">
                            <p style={{ ...courierNormal, textAlign: 'justify', marginBottom: '4px' }}>
                              <span style={courierBold}>CLÁUSULA 8ª</span> - O CONTRATANTE compromete-se a manter a disposição da CONTRATADA todos os meios necessários para execução dos serviços, ou seja, energia elétrica, iluminação, 04 cadeiras, local adequado para a realização das atividades e lanche para os Recreadores.
                            </p>
                          </div>

                          <div className="mb-1">
                            <p style={{ ...courierNormal, textAlign: 'justify', marginBottom: '4px' }}>
                              <span style={courierBold}>CLÁUSULA 9ª</span> - Se o evento for realizado em condomínio ou prédio, o CONTRATANTE deverá avisar com antecedência aos seguranças e à portaria a chegada dos funcionários da CONTRATADA, facilitando a entrada com veículo nas dependências, para carga e descarga de material.
                            </p>
                          </div>

                          <div className="mb-1">
                            <p style={{ ...courierNormal, textAlign: 'justify', marginBottom: '4px' }}>
                              <span style={courierBold}>CLÁUSULA 10ª</span> - O CONTRATANTE deverá efetuar o pagamento na forma e condições estabelecidas na cláusula 14ª.
                            </p>
                          </div>

                          <div className="mb-1">
                            <p style={{ ...courierNormal, textAlign: 'justify', marginBottom: '4px' }}>
                              <span style={courierBold}>CLÁUSULA 11ª</span> - O CONTRATANTE deverá avisar com antecedência de no mínimo 5 dias em caso de alteração da data e horário da festa para que seja acordada nova data e horário, disponível com a agenda da CONTRATADA, desde que haja disponibilidade na mesma.
                            </p>
                          </div>
                        </div>

                        {/* Obrigações da Contratada */}
                        <div className="mb-1">
                          <h2 style={{ ...courierBold, fontSize: '10pt', marginBottom: '6px' }}>
                            DAS OBRIGAÇÕES DA CONTRATADA
                          </h2>
                          
                          <div className="mb-1">
                            <p style={{ ...courierNormal, textAlign: 'justify', marginBottom: '4px' }}>
                              <span style={courierBold}>CLÁUSULA 12ª</span> - É dever da CONTRATADA oferecer ao contratante a cópia do presente instrumento, contendo todas as especificidades da prestação de serviço contratado.
                            </p>
                          </div>

                          <div className="mb-1">
                            <p style={{ ...courierNormal, textAlign: 'justify', marginBottom: '4px' }}>
                              <span style={courierBold}>CLÁUSULA 13ª</span> - Se houver atraso por parte da CONTRATADA, esta deverá compensar ao final da recreação.
                            </p>
                          </div>
                        </div>

                        {/* Preço e Condições de Pagamento */}
                        <div className="mb-1">
                          <h2 style={{ ...courierBold, fontSize: '10pt', marginBottom: '6px' }}>
                            DO PREÇO E DAS CONDIÇÕES DE PAGAMENTO
                          </h2>
                          
                          <div className="mb-1">
                            <p style={{ ...courierNormal, textAlign: 'justify', marginBottom: '4px' }}>
                              <span style={courierBold}>CLÁUSULA 14ª</span> - O presente serviço será remunerado pela quantia de R$ {contract.valor} ({formatValueLong(contract.valor)}), devendo ser pago no ato da assinatura do contrato. O contrato só terá valor mediante envio do comprovante de pagamento do valor estipulado acima. Conta para transferência:
                            </p>
                          </div>

                          {/* Dados Bancários */}
                          <div className="mb-1" style={{ backgroundColor: '#f8fafc', padding: '12px', border: '1px solid #e2e8f0', borderRadius: '4px' }}>
                            <div className="text-right mb-2">
                              <span style={{ ...courierBold, color: '#0f9d8f' }}>BANCO CC CREDILIVRE LTDA.</span>
                            </div>
                            <div style={courierNormal}>
                              <p>SICOOB CREDILIVRE, AG 3049, CONTA CORRENTE 57 559 3, BANCO: 756, CNPJ: 31 692</p>
                              <p>815 0001-02 ou pode ser enviado através do PIX 31 692 815 0001 02</p>
                              <p className="text-right" style={{ color: '#64748b' }}>(conferir cuidadosamente as informações antes de transferir).</p>
                            </div>
                          </div>

                          <div className="mb-1">
                            <p style={{ ...courierNormal, textAlign: 'justify', marginBottom: '4px' }}>
                              <span style={courierBold}>Parágrafo único.</span> Pagamentos onde houver parcelas, o mesmo deverá ser feito no dia estipulado, o não pagamento em dia acarretará multa diária de R$ 15,00.
                            </p>
                          </div>
                        </div>
                      </div>
                      
                      {/* Imagem de Rodapé */}
                      <img 
                        src="/images/rodape.jpg" 
                        alt="Rodapé" 
                        className="absolute bottom-0 left-0 w-full h-auto"
                        style={{ display: 'block' }}
                      />
                    </div>
                  );

                case 3:
                  return (
                    <div className="bg-white shadow-lg border border-slate-200 rounded-lg overflow-hidden relative" 
                         style={{ width: '210mm', height: '297mm', transform: 'scale(1)', transformOrigin: 'top left' }}>
                      {/* Imagem de Cabeçalho */}
                      <img 
                        src="/images/cabecalho.jpg" 
                        alt="Cabeçalho" 
                        className="w-full h-auto"
                        style={{ display: 'block' }}
                      />
                      
                      <div style={{ padding: '12mm', paddingBottom: '5px', ...courierNormal, fontSize: '10pt', lineHeight: '1.4' }}>
                        {/* Rescisão e Forças Maiores */}
                        <div className="mb-1">
                          <h2 style={{ ...courierBold, fontSize: '10pt', marginBottom: '6px' }}>
                            DA RESCISÃO IMOTIVADA ou POR CASOS FORTUITOS e FORÇAS MAIORES
                          </h2>
                          
                          <div className="mb-1">
                            <p style={{ ...courierNormal, textAlign: 'justify', marginBottom: '4px' }}>
                              <span style={courierBold}>CLÁUSULA 15ª</span> - Se a desistência for por parte do CONTRATANTE haverá a perda do sinal dado.
                            </p>
                          </div>

                          <div className="mb-1">
                            <p style={{ ...courierNormal, textAlign: 'justify', marginBottom: '4px' }}>
                              <span style={courierBold}>Parágrafo único.</span> Caso o evento não possa ser realizado no dia e hora estipulada entre as partes por forças maiores e em casos fortuitos a data poderá ser alterada em comum acordo.
                            </p>
                          </div>

                          <div className="mb-1">
                            <p style={{ ...courierNormal, textAlign: 'justify', marginBottom: '4px' }}>
                              <span style={courierBold}>CLÁUSULA 16ª</span> - Se a desistência for por parte da CONTRATADA, esta deverá ressarcir o valor do sinal dado pelo CONTRATANTE, salvo se ocorrer o previsto na cláusula 11ª, não cabendo, neste caso, nem a devolução de sinal.
                            </p>
                          </div>
                        </div>

                        {/* Condições Gerais */}
                        <div className="mb-1">
                          <h2 style={{ ...courierBold, fontSize: '10pt', marginBottom: '6px' }}>
                            DAS CONDIÇÕES GERAIS
                          </h2>
                          
                          <div className="mb-1">
                            <p style={{ ...courierNormal, textAlign: 'justify', marginBottom: '4px' }}>
                              <span style={courierBold}>CLÁUSULA 17ª</span> - Fica compactuada entre as partes a total inexistência de vínculo trabalhista entre as partes contratantes, excluindo as obrigações previdenciárias e os encargos sociais, não havendo entre CONTRATADA e CONTRATANTE qualquer tipo de relação de subordinação.
                            </p>
                          </div>

                          <div className="mb-1">
                            <p style={{ ...courierNormal, textAlign: 'justify', marginBottom: '4px' }}>
                              <span style={courierBold}>CLÁUSULA 18ª</span> - Salvo com a expressa autorização do CONTRATANTE, não pode a CONTRATADA transferir ou subcontratar os serviços previstos neste instrumento, sob o risco de ocorrer a rescisão imediata.
                            </p>
                          </div>

                          <div className="mb-1">
                            <p style={{ ...courierNormal, textAlign: 'justify', marginBottom: '4px' }}>
                              <span style={courierBold}>CLÁUSULA 19ª</span> - AUTORIZO a contratante expor fotos do evento.
                            </p>
                          </div>
                        </div>

                        {/* Foro */}
                        <div className="mb-1">
                          <h2 style={{ ...courierBold, fontSize: '10pt', marginBottom: '6px' }}>
                            DO FORO
                          </h2>
                          
                          <div className="mb-1">
                            <p style={{ ...courierNormal, textAlign: 'justify', marginBottom: '4px' }}>
                              <span style={courierBold}>CLÁUSULA 20ª</span> - Para dirimir quaisquer controvérsias oriundas do presente contrato, as partes elegem o foro da comarca de Manhuaçu/MG.
                            </p>
                          </div>
                        </div>

                        {/* Fechamento */}
                        <div className="mt-8" style={{ borderTop: '1px solid #e2e8f0', paddingTop: '20px' }}>
                          <p style={{ ...courierNormal, textAlign: 'center', marginBottom: '15px' }}>
                            Por estarem assim justos e contratados, firmam o presente instrumento, em duas vias de igual teor.
                          </p>

                          {/* Local e Data */}
                          <div className="text-center mb-8">
                            <p style={courierNormal}>
                              Manhuaçu, {formatDateLong(new Date().toISOString())}.
                            </p>
                          </div>

                          {/* Assinaturas */}
                          <div className="grid grid-cols-2 gap-8" style={{ marginTop: '20px' }}>
                            {/* Contratada */}
                            <div className="text-center">
                              <div style={{ borderTop: '2px solid #64748b', paddingTop: '8px', marginBottom: '4px' }}>
                                <p style={courierBold}>CONTRATADA:</p>
                              </div>
                              <p style={courierNormal}>RODRIGO PEREIRA DE OLIVEIRA</p>
                              <p style={{ ...courierNormal, fontSize: '10pt', color: '#64748b' }}>CPF: 016.331.906-50</p>
                            </div>

                            {/* Contratante */}
                            <div className="text-center">
                              <div style={{ borderTop: '2px solid #64748b', paddingTop: '8px', marginBottom: '4px' }}>
                                <p style={courierBold}>CONTRATANTE:</p>
                              </div>
                              <p style={courierNormal}>{contract.contratante?.toUpperCase()}</p>
                              <p style={{ ...courierNormal, fontSize: '10pt', color: '#64748b' }}>{(() => {
                                // Verificar se tem CNPJ primeiro (prioridade)
                                if (contract.cnpj && contract.cnpj.trim() !== '') {
                                  return `CNPJ: ${contract.cnpj}`;
                                }
                                // Se não tem CNPJ, verificar CPF
                                if (contract.cpf && contract.cpf.trim() !== '') {
                                  return `CPF: ${contract.cpf}`;
                                }
                                // Se não tem nenhum, mostrar N/A
                                return 'CPF: N/A';
                              })()}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Imagem de Rodapé */}
                      <img 
                        src="/images/rodape.jpg" 
                        alt="Rodapé" 
                        className="absolute bottom-0 left-0 w-full h-auto"
                        style={{ display: 'block' }}
                      />
                    </div>
                  );

                default:
                  return null;
              }
            };

            return renderPage(pageNumber);
          }}
        />
      </div>
    </div>
  );
};

export default ContractView;
